"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.decodePolyline = decodePolyline;
exports.encodePolyline = encodePolyline;
/**
 * Decodes a Google encoded polyline string into an array of coordinates.
 *
 * Google's Polyline Algorithm encodes coordinates as a series of characters
 * representing the differences between consecutive points.
 *
 * @see https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 *
 * @param encoded - The encoded polyline string from Google Routes API
 * @returns Array of decoded latitude/longitude coordinates
 *
 * @example
 * const encoded = "_p~iF~ps|U_ulLnnqC_mqNvxq`@";
 * const coords = decodePolyline(encoded);
 * // Returns: [
 * //   { latitude: 38.5, longitude: -120.2 },
 * //   { latitude: 40.7, longitude: -120.95 },
 * //   { latitude: 43.252, longitude: -126.453 }
 * // ]
 */
function decodePolyline(encoded) {
    if (!encoded || encoded.length === 0) {
        return [];
    }
    const coordinates = [];
    let index = 0;
    let lat = 0;
    let lng = 0;
    while (index < encoded.length) {
        // Decode latitude
        let shift = 0;
        let result = 0;
        let byte;
        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);
        const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
        lat += deltaLat;
        // Decode longitude
        shift = 0;
        result = 0;
        do {
            byte = encoded.charCodeAt(index++) - 63;
            result |= (byte & 0x1f) << shift;
            shift += 5;
        } while (byte >= 0x20);
        const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
        lng += deltaLng;
        // Convert from integer representation (1e5 precision) to decimal degrees
        coordinates.push({
            latitude: lat / 1e5,
            longitude: lng / 1e5,
        });
    }
    return coordinates;
}
/**
 * Encodes an array of coordinates into a Google encoded polyline string.
 * This is the inverse of decodePolyline.
 *
 * @param coordinates - Array of latitude/longitude coordinates
 * @returns Encoded polyline string
 */
function encodePolyline(coordinates) {
    if (!coordinates || coordinates.length === 0) {
        return "";
    }
    let encoded = "";
    let prevLat = 0;
    let prevLng = 0;
    for (const coord of coordinates) {
        const lat = Math.round(coord.latitude * 1e5);
        const lng = Math.round(coord.longitude * 1e5);
        encoded += encodeSignedNumber(lat - prevLat);
        encoded += encodeSignedNumber(lng - prevLng);
        prevLat = lat;
        prevLng = lng;
    }
    return encoded;
}
/**
 * Encodes a signed number for polyline encoding
 */
function encodeSignedNumber(num) {
    let sgnNum = num << 1;
    if (num < 0) {
        sgnNum = ~sgnNum;
    }
    let encoded = "";
    while (sgnNum >= 0x20) {
        encoded += String.fromCharCode((0x20 | (sgnNum & 0x1f)) + 63);
        sgnNum >>= 5;
    }
    encoded += String.fromCharCode(sgnNum + 63);
    return encoded;
}
